import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorageManager } from './useLocalStorageManager';
import { uiLogger } from '@/lib/structuredLogger';
import type { LifecycleStageBuild, LifecycleStageTender } from '@/constants/lifecycleStages';

export interface ProjectFiltersData {
  searchQuery: string;
  projectGroup: string;
  contractYear?: string[];
  projectType: string[];
  teamLead: string;
  status: string[];
  company: string; // 'ENP' | 'Solid' | 'Unassigned' | ''
  projectRoleUserIds?: string[];
  projectMark?: string;
  projectRoleTypes?: string[];
  lifecycleStageBuild?: LifecycleStageBuild | 'unset' | '';
  lifecycleStageTender?: LifecycleStageTender | 'unset' | '';
}

const DEFAULT_FILTERS: ProjectFiltersData = {
  searchQuery: '',
  projectGroup: '',
  contractYear: [],
  projectType: [],
  teamLead: '',
  status: [],
  company: '',
  projectRoleUserIds: [],
  projectMark: '',
  projectRoleTypes: [],
  lifecycleStageBuild: '',
  lifecycleStageTender: ''
};

const parseArrayParam = (value: string | null): string[] => {
  if (!value) return [];
  return value.split(',').filter(Boolean);
};

const serializeArrayParam = (arr: string[]): string | null => {
  if (!arr || arr.length === 0) return null;
  return arr.join(',');
};

const validateFilters = (filters: any): ProjectFiltersData => {
  if (!filters || typeof filters !== 'object') {
    return DEFAULT_FILTERS;
  }
  
  const safeStatus = Array.isArray(filters.status) 
    ? filters.status.filter((s: string) => s !== 'service' && s !== 'inactive')
    : [];
  
  return {
    searchQuery: typeof filters.searchQuery === 'string' ? filters.searchQuery.slice(0, 100) : '',
    projectGroup: typeof filters.projectGroup === 'string' ? filters.projectGroup : '',
    contractYear: Array.isArray(filters.contractYear) ? filters.contractYear : [],
    projectType: Array.isArray(filters.projectType) ? filters.projectType : [],
    teamLead: typeof filters.teamLead === 'string' ? filters.teamLead : '',
    status: safeStatus,
    company: typeof filters.company === 'string' ? filters.company : '',
    projectRoleUserIds: Array.isArray(filters.projectRoleUserIds) ? filters.projectRoleUserIds : [],
    projectMark: typeof filters.projectMark === 'string' ? filters.projectMark : '',
    projectRoleTypes: Array.isArray(filters.projectRoleTypes) ? filters.projectRoleTypes : [],
    lifecycleStageBuild: (typeof filters.lifecycleStageBuild === 'string' ? filters.lifecycleStageBuild : '') as ProjectFiltersData['lifecycleStageBuild'],
    lifecycleStageTender: (typeof filters.lifecycleStageTender === 'string' ? filters.lifecycleStageTender : '') as ProjectFiltersData['lifecycleStageTender']
  };
};

const parseFiltersFromUrl = (searchParams: URLSearchParams): ProjectFiltersData => {
  return validateFilters({
    searchQuery: searchParams.get('search') || '',
    projectGroup: searchParams.get('group') || '',
    contractYear: searchParams.get('contract_year')?.split(',').filter(Boolean) || [],
    projectType: parseArrayParam(searchParams.get('types')),
    teamLead: searchParams.get('lead') || '',
    status: parseArrayParam(searchParams.get('status')),
    company: searchParams.get('company') || '',
    projectRoleUserIds: parseArrayParam(searchParams.get('roleUsers')),
    projectMark: searchParams.get('mark') || '',
    projectRoleTypes: parseArrayParam(searchParams.get('roleTypes')),
    lifecycleStageBuild: (searchParams.get('build_stage') || '') as ProjectFiltersData['lifecycleStageBuild'],
    lifecycleStageTender: (searchParams.get('tender_stage') || '') as ProjectFiltersData['lifecycleStageTender']
  });
};

export const useProjectFiltersState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const localStorage = useLocalStorageManager();
  const hasMigratedRef = useRef(false);
  
  useEffect(() => {
    if (hasMigratedRef.current) return;
    hasMigratedRef.current = true;
    
    const hasUrlFilters = searchParams.has('search') || searchParams.has('types') || 
                          searchParams.has('status') || searchParams.has('company') ||
                          searchParams.has('group') || searchParams.has('lead') ||
                          searchParams.has('mark') || searchParams.has('roleUsers') ||
                          searchParams.has('roleTypes') || searchParams.has('build_stage') ||
                          searchParams.has('tender_stage') || searchParams.has('contract_year');
    
    if (!hasUrlFilters) {
      try {
        const saved = localStorage.getStandardValue('project-filters', null, 'USER');
        if (saved && typeof saved === 'object') {
          const validated = validateFilters(saved);
          const hasAnyFilter = Object.values(validated).some(v => 
            (Array.isArray(v) && v.length > 0) || (typeof v === 'string' && v !== '')
          );
          
          if (hasAnyFilter) {
            uiLogger.info('Migrating project filters from localStorage to URL', { filters: validated });
            setSearchParams(params => {
              if (validated.searchQuery) params.set('search', validated.searchQuery);
              if (validated.projectGroup) params.set('group', validated.projectGroup);
              if ((validated.contractYear?.length ?? 0) > 0) params.set('contract_year', (validated.contractYear || []).join(','));
              const types = serializeArrayParam(validated.projectType);
              if (types) params.set('types', types);
              if (validated.teamLead) params.set('lead', validated.teamLead);
              const status = serializeArrayParam(validated.status);
              if (status) params.set('status', status);
              if (validated.company) params.set('company', validated.company);
              const roleUsers = serializeArrayParam(validated.projectRoleUserIds || []);
              if (roleUsers) params.set('roleUsers', roleUsers);
              if (validated.projectMark) params.set('mark', validated.projectMark);
              const roleTypes = serializeArrayParam(validated.projectRoleTypes || []);
              if (roleTypes) params.set('roleTypes', roleTypes);
              if (validated.lifecycleStageBuild) params.set('build_stage', validated.lifecycleStageBuild);
              if (validated.lifecycleStageTender) params.set('tender_stage', validated.lifecycleStageTender);
              return params;
            }, { replace: true });
          }
          
          localStorage.removeStandardValue('project-filters', 'USER');
        }
      } catch (error) {
        uiLogger.warn('Failed to migrate project filters from localStorage', { error });
      }
    }
  }, [searchParams, setSearchParams, localStorage]);
  
  const filters = parseFiltersFromUrl(searchParams);
  
  const updateFilters = useCallback((newFilters: ProjectFiltersData) => {
    const validated = validateFilters(newFilters);
    
    setSearchParams(params => {
      if (validated.searchQuery) params.set('search', validated.searchQuery);
      else params.delete('search');
      
      if (validated.projectGroup) params.set('group', validated.projectGroup);
      else params.delete('group');

      if ((validated.contractYear?.length ?? 0) > 0) params.set('contract_year', (validated.contractYear || []).join(','));
      else params.delete('contract_year');
      
      const types = serializeArrayParam(validated.projectType);
      if (types) params.set('types', types);
      else params.delete('types');
      
      if (validated.teamLead) params.set('lead', validated.teamLead);
      else params.delete('lead');
      
      const status = serializeArrayParam(validated.status);
      if (status) params.set('status', status);
      else params.delete('status');
      
      if (validated.company) params.set('company', validated.company);
      else params.delete('company');
      
      const roleUsers = serializeArrayParam(validated.projectRoleUserIds || []);
      if (roleUsers) params.set('roleUsers', roleUsers);
      else params.delete('roleUsers');
      
      if (validated.projectMark) params.set('mark', validated.projectMark);
      else params.delete('mark');
      
      const roleTypes = serializeArrayParam(validated.projectRoleTypes || []);
      if (roleTypes) params.set('roleTypes', roleTypes);
      else params.delete('roleTypes');

      if (validated.lifecycleStageBuild) params.set('build_stage', validated.lifecycleStageBuild);
      else params.delete('build_stage');

      if (validated.lifecycleStageTender) params.set('tender_stage', validated.lifecycleStageTender);
      else params.delete('tender_stage');
      
      return params;
    }, { replace: true });
    
    uiLogger.info('Project filters updated', {
      previousFilters: filters,
      newFilters: validated,
      timestamp: Date.now()
    });
  }, [setSearchParams, filters]);
  
  const clearFilters = useCallback(() => {
    setSearchParams(params => {
      params.delete('search');
      params.delete('group');
      params.delete('contract_year');
      params.delete('types');
      params.delete('lead');
      params.delete('status');
      params.delete('company');
      params.delete('roleUsers');
      params.delete('mark');
      params.delete('roleTypes');
      params.delete('build_stage');
      params.delete('tender_stage');
      return params;
    }, { replace: true });
    
    uiLogger.info('Project filters cleared');
  }, [setSearchParams]);
  
  const updateSingleFilter = useCallback((key: keyof ProjectFiltersData, value: string) => {
    const newFilters = { ...filters, [key]: value };
    updateFilters(newFilters);
  }, [filters, updateFilters]);
  
  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.projectGroup) count++;
    if ((filters.contractYear?.length ?? 0) > 0) count++;
    if (filters.projectType.length > 0) count++;
    if (filters.teamLead) count++;
    if (filters.status.length > 0) count++;
    if (filters.company) count++;
    if (filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0) count++;
    if (filters.projectMark) count++;
    if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) count++;
    if (filters.lifecycleStageBuild) count++;
    if (filters.lifecycleStageTender) count++;
    return count;
  }, [filters]);
  
  return {
    filters,
    updateFilters,
    clearFilters,
    updateSingleFilter,
    getActiveFiltersCount,
    isDefaultState: JSON.stringify(filters) === JSON.stringify(DEFAULT_FILTERS)
  };
};
